import { telefonoParaMostrar } from "../../../helpers/telefonoWhatsapp";
import React, { useEffect, useRef, useState } from "react";
import ClientePage from "../../clientes/ClientePage";
import VincularFichaModal from "../../clientes/VincularFichaModal";
import { FilaHistorial } from "../../clientes/ClienteHistorial";
import { useClientesApi } from "../../clientes/clientesApi";
import { errorDeApi } from "../../clientes/clientesUtils";
import { ConfirmModal } from "../TurnosUi";
import "../../clientes/clientes.css";

// Textos que cambian según la ficha se abra desde un turno de Beauty o desde una reserva de cancha o mesa.
const TEXTOS = {
  turno: {
    noDisponible: "El turno ya no está disponible. Cerrá y volvé a abrirlo.",
    datos: "Datos de contacto registrados en este turno.",
    cierre: "Cerrar ficha y volver al turno",
    volver: "Volver al turno",
    sinCuenta: "Este turno no tiene una cuenta asociada. Mostramos solo este turno; no vinculamos clientes por nombre, email o teléfono.",
    historial: "Turnos del cliente",
    truncado: "Mostramos los 100 turnos más recientes de este alcance.",
    desvinculado: "El turno ya no está vinculado a esa ficha.",
    vinculado: "Vinculamos el turno a la ficha.",
    errorDesvincular: "No pudimos desvincular el turno. Probá de nuevo.",
    desvincularTitulo: "Desvincular turno",
    desvincularTexto: "Este turno deja de estar asociado a la ficha. La ficha y sus notas no se borran. Si el turno es de una cuenta de Zoco, sigue asociado a la ficha de esa cuenta.",
  },
  reserva: {
    noDisponible: "La reserva ya no está disponible. Cerrá y volvé a abrirla.",
    datos: "Datos de contacto registrados en esta reserva.",
    cierre: "Cerrar ficha y volver a la reserva",
    volver: "Volver a la reserva",
    sinCuenta: "Esta reserva no tiene una cuenta asociada. Mostramos solo esta reserva; no vinculamos clientes por nombre, email o teléfono.",
    historial: "Reservas del cliente",
    truncado: "Mostramos las 100 reservas más recientes de este alcance.",
    desvinculado: "La reserva ya no está vinculada a esa ficha.",
    vinculado: "Vinculamos la reserva a la ficha.",
    errorDesvincular: "No pudimos desvincular la reserva. Probá de nuevo.",
    desvincularTitulo: "Desvincular reserva",
    desvincularTexto: "Esta reserva deja de estar asociada a la ficha. La ficha y sus notas no se borran. Si la reserva es de una cuenta de Zoco, sigue asociada a la ficha de esa cuenta.",
  },
};

const mensajeDeAccion = (e, fallback, textos) => {
  const { message, status } = errorDeApi(e, fallback);
  if (status === 403) return "No tenés permiso para esta acción.";
  if (status === 404) return textos.noDisponible;
  return message;
};


// Appointment snapshot and history, plus the entry points to the client record: open it, create it, link it or (owner) unlink it.
// Identity and access scope are resolved by the server; nothing is joined by name, email or phone.
const FichaCliente = ({ reservationId, onVolver, onClose, puedeDesvincular = false, tipo = "turno" }) => {
  const api = useClientesApi();
  const textos = TEXTOS[tipo] || TEXTOS.turno;
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState({ loading: true, info: null, error: false });
  const [completa, setCompleta] = useState(false);
  const [vinculando, setVinculando] = useState(false);
  const [desvinculando, setDesvinculando] = useState(false);
  const [accion, setAccion] = useState({ busy: false, error: "", aviso: "" });
  const dialogRef = useRef(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    let active = true;
    setState({ loading: true, info: null, error: false });
    const load = async () => {
      try {
        const response = await api.reservationClient(reservationId);
        const data = response?.data;
        if (data?.isSuccess !== true || !data.info?.client || !Array.isArray(data.info.items ?? data.info.appointments)) {
          throw new Error("Invalid client profile response");
        }
        if (active) setState({ loading: false, info: data.info, error: false });
      } catch {
        if (active) setState({ loading: false, info: null, error: true });
      }
    };
    load();
    return () => { active = false; };
  }, [api, reservationId, retry]);

  const recargar = (aviso = "", error = "") => {
    setAccion({ busy: false, error, aviso });
    setRetry((value) => value + 1);
  };

  // An appointment of a Zoco account opens (or creates) that account's record with a plain POST;
  // a guest or counter appointment needs `create: true` and only then the record is created.
  const crearFicha = async () => {
    setAccion({ busy: true, error: "", aviso: "" });
    try {
      await api.openReservationClient(reservationId, state.info.linked ? {} : { create: true });
      recargar("La ficha del cliente está lista.");
    } catch (e) {
      setAccion({ busy: false, error: mensajeDeAccion(e, "No pudimos crear la ficha. Probá de nuevo.", textos), aviso: "" });
    }
  };

  const desvincular = async () => {
    setAccion({ busy: true, error: "", aviso: "" });
    try {
      await api.unlinkReservation(reservationId);
      setDesvinculando(false);
      recargar(textos.desvinculado);
    } catch (e) {
      setDesvinculando(false);
      setAccion({ busy: false, error: mensajeDeAccion(e, textos.errorDesvincular, textos), aviso: "" });
    }
  };

  const info = state.info;

  if (completa && info?.clientId) {
    return (
      <div className="zr-overlay" onMouseDown={onClose}>
        <div
          className="zr-modal fc-modal-wide"
          role="dialog"
          aria-modal="true"
          aria-label="Ficha completa del cliente"
          onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); setCompleta(false); } }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <ClientePage
            clientId={info.clientId}
            volverLabel={textos.volver}
            onVolver={() => { setCompleta(false); recargar(); }}
            onArchivado={() => { setCompleta(false); recargar("La ficha se archivó."); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="zr-overlay" onMouseDown={onClose}>
      <div
        className="zr-modal ba-modal ba-modal--detalle ba-client-profile"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ba-client-profile-title"
        aria-busy={state.loading}
        ref={dialogRef}
        tabIndex={-1}
        onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); onClose(); } }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" className="ba-modal__close" aria-label={textos.cierre} onClick={onVolver}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
        <h3 id="ba-client-profile-title" className="zr-modal__title">Ficha del cliente</h3>
        {state.loading && <p role="status">Cargando ficha…</p>}
        {state.error && (
          <div>
            <p role="alert">No pudimos cargar la ficha. Probá de nuevo.</p>
            <button type="button" className="zr-btn zr-btn--soft" onClick={() => setRetry((value) => value + 1)}>Reintentar</button>
          </div>
        )}
        {info && (
          <>
            <p className="zr-modal__text">{textos.datos}</p>
            <dl className="ba-detalle">
              <dt>Nombre</dt><dd>{info.client.name || "Sin nombre"}</dd>
              <dt>Email</dt><dd>{info.client.email || "—"}</dd>
              <dt>Teléfono</dt><dd>{telefonoParaMostrar(info.client.phone) || "—"}</dd>
            </dl>

            <div className="fc-actions-inline">
              {info.clientId && (
                <button type="button" className="zr-btn zr-btn--lime" onClick={() => setCompleta(true)}>Ver ficha completa</button>
              )}
              {!info.clientId && info.canCreate && (
                <button type="button" className="zr-btn zr-btn--lime" disabled={accion.busy} onClick={crearFicha}>Crear ficha</button>
              )}
              {!info.clientId && info.canCreate && !info.linked && (
                <button type="button" className="zr-btn zr-btn--soft" disabled={accion.busy} onClick={() => setVinculando(true)}>Vincular a ficha</button>
              )}
              {info.clientId && puedeDesvincular && info.linkedBy !== "account" && (
                <button type="button" className="zr-btn zr-btn--ghost" disabled={accion.busy} onClick={() => setDesvinculando(true)}>Desvincular</button>
              )}
            </div>
            {!info.clientId && !info.canCreate && <p className="ba-aviso">Este cliente todavía no tiene ficha y tu usuario no puede crearla.</p>}
            {accion.error && <p role="alert" className="fc-field-error">{accion.error}</p>}
            {accion.aviso && <p role="status" className="fc-hint">{accion.aviso}</p>}

            {!info.linked && <p className="ba-aviso">{textos.sinCuenta}</p>}
            {info.scope === "own" && <p className="ba-aviso">Solo se muestran los turnos de tu agenda.</p>}
            <h4>{textos.historial}</h4>
            {info.truncated && <p className="ba-aviso">{textos.truncado}</p>}
            <ul className="ba-client-profile__history" aria-label={textos.historial}>
              {(info.items ?? info.appointments).map((fila) => <FilaHistorial key={fila._id} fila={fila} />)}
            </ul>
          </>
        )}
        <div className="zr-modal__actions zr-modal__actions--reservation-detail">
          <button type="button" className="zr-btn zr-btn--ghost zr-modal__action" onClick={onVolver}>{textos.volver}</button>
          <button type="button" className="zr-btn zr-btn--dark zr-modal__action" onClick={onClose}>Cerrar ficha</button>
        </div>
      </div>

      {vinculando && (
        <VincularFichaModal
          reservationId={reservationId}
          tipo={tipo}
          onCerrar={() => setVinculando(false)}
          onVinculado={() => { setVinculando(false); recargar(textos.vinculado); }}
          onCuenta={(mensaje) => { setVinculando(false); recargar("", mensaje); }}
        />
      )}
      {desvinculando && (
        <ConfirmModal
          title={textos.desvincularTitulo}
          confirmLabel="Desvincular"
          busy={accion.busy}
          onConfirm={desvincular}
          onCancel={() => setDesvinculando(false)}
        >
          {textos.desvincularTexto}
        </ConfirmModal>
      )}
    </div>
  );
};

export default FichaCliente;
